import React, { useEffect, useState, Fragment } from 'react';
import { useIntl } from 'react-intl';
import { differenceWith, union, isArray, each, uniqBy, values, reduce, find, filter } from 'lodash';
import {
  Button,
  Grid,
} from '@material-ui/core';
import PropTypes from 'prop-types';

import {
  FormActionButtons,
  ErrorContainer,
  SearchTextListContainer,
} from '../../../../components';
import { getI18n } from '../../../../utils/intl';

import {
  ERROR_TEXT,
  SAVE_TEXT,
  TAGS_SUB_TITLE,
  TAGS_PLACE_HOLDER_TEXT,
  TAGS_SELECTED_TAG_SUB_TITLE,
  TAGS_ADD_NEW_TAG,
  TAGS_SELECT_SWAGGER_TAGS,
  TAGS_UNSELECT_SWAGGER_TAGS,
  TAGS_SOURCE_SWAGGER_TEXT,
  TAGS_SOURCE_PORTAL_TEXT,
  TAGS_IMPORT_SWAGGER_TAGS_ERROR,
  TAGS_ASSOCIATION_LIMIT_MSG,
  TAGS_INVALID_ERROR,
} from '../labels';

const MAX_TAG_LENGTH = 60;
const LIMIT_TAGS_ASSOCIATED = 25;
const TAGS_SOURCE_PORTAL = 'portal';
const TAGS_SOURCE_SWAGGER = 'swagger';

const getTagsNamesFromSwaggerTagObjects = (tagObjects = []) => tagObjects.map(item => item.name);

// tag max length MAX_TAG_LENGTH, allowed characters a-z,A-Z,0-9,.,_,#,",:,',-.
const validateTag = (newTag) => (
  (newTag && (newTag.length <= MAX_TAG_LENGTH)) &&
  /^[a-zA-Z0-9._#" :'-]*$/.test(newTag)
);

const tagsEqual = (tag1, tag2) =>
  ((tag1 && tag1.toLowerCase()) === (tag2 && tag2.toLowerCase()));

// truncates strings longer than MAX_TAG_LENGTH
// with ellipsis at the end
const truncateSwaggerTag = tag => {
  let trimmedTag = tag.trim();
  // add ellipsis at the end if the tag size > 60
  if (trimmedTag && trimmedTag.length > MAX_TAG_LENGTH) {
    trimmedTag = `${trimmedTag.substring(0, MAX_TAG_LENGTH - 3)}...`;
  }
  return trimmedTag;
};

const extractTagsFromSwaggerData = (swaggerData = {}) => {
  const rootTagObjects = swaggerData.tags || [];
  let tagsNames = getTagsNamesFromSwaggerTagObjects(rootTagObjects);
  const pathsObject = (swaggerData && swaggerData.paths) || {};
  // iterate over op objects in each path object
  each(pathsObject, (pathObject) => {
    each(pathObject, (opObject) => {
      tagsNames = tagsNames.concat(opObject.tags || []);
    });
  });
  return tagsNames;
};

const getTagsNamesFromSwaggger = (swaggerData = {}) => {
  let tagsNames = extractTagsFromSwaggerData(swaggerData);

  // removing empty tags
  tagsNames = tagsNames.filter(tag => tag && tag.trim());
  // remove duplicate tags
  tagsNames = uniqBy(tagsNames);
  const tagsBeforeValidation = tagsNames.length;
  // truncate longer tags
  tagsNames = tagsNames.map(truncateSwaggerTag);
  // removing invalid tags
  tagsNames = tagsNames.filter(tag => validateTag(tag));
  // get unique tags, ignoring case
  // this unique operation discards duplicate tags by using caseinsenstive match.
  // we are doing it here, instead of using caseinsenstive match initially,
  // as we want to inform user about tags discarded due caseinsenstive match.
  tagsNames = uniqBy(tagsNames, tag => tag.toLowerCase());
  const tagsAfterValidation = tagsNames.length;
  const tagsDiscarded = tagsBeforeValidation !== tagsAfterValidation;
  return {
    discarded: tagsDiscarded,
    tags: tagsNames,
  };
};

const getSwaggerOnlyTagsNames = (tags) => {
  const swaggerOnlyTags = tags.filter(tagObj =>
    ((tagObj.sources.length === 1) && (tagObj.sources[0] === TAGS_SOURCE_SWAGGER)));
  return swaggerOnlyTags.map(item => item.name);
};

const getExistingTagNames = (tags) => {
  const nonSwaggerOnlyTags = tags.filter(tagObj =>
    ((tagObj.sources.length !== 1) || (tagObj.sources[0] !== TAGS_SOURCE_SWAGGER)));
  return nonSwaggerOnlyTags.map(item => item.name);
};

const getItemSecondaryText = (item, portalText, swaggerText) => {
  const textArray = [];
  const { sources = [] } = item;
  each(sources, (source) => {
    if (source === TAGS_SOURCE_PORTAL) {
      textArray.push(portalText);
    } else if (source === TAGS_SOURCE_SWAGGER) {
      textArray.push(swaggerText);
    }
  });
  return textArray.join(',');
};

const getDummyTagsFromNames = (tagNames = []) =>
  tagNames.map((name, i) => ({
    name,
    uuid: `dummy-uuid-${i}`,
  }));

export default function Tags(props) {
  const {
    classes,
    apiUuid,
    allPortalTags,
    apiDetails,
    apiTags,
    assets,
    newTags,
    errors,
    swaggerData,
    createTags,
    notifyMessages,
    onCancel,
    visible,
  } = props;

  const intl = getI18n(useIntl());
  const [selectedTags, setSelectedTags] = useState([]);
  const [createdTags, setCreatedTags] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isNextPending, setIsNextPending] = useState(false);
  const [defaultListTags, setDefaultListTags] = useState([]);
  const [hasSelectionChanged, setHasSelectionChanged] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [errsInTagImport, setErrsInTagImport] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const [showingTAGLimitErr, setShowingTAGLimitErr] = useState(false);
  const portalText = intl.getI18nMessage(TAGS_SOURCE_PORTAL_TEXT);
  const swaggerText = intl.getI18nMessage(TAGS_SOURCE_SWAGGER_TEXT);

  useEffect(() => {
    if (errsInTagImport && visible) {
      const swaggerImporError =
        intl.getI18nMessage(TAGS_IMPORT_SWAGGER_TAGS_ERROR);
      notifyMessages(
        swaggerImporError,
        ERROR_TEXT,
      );
    }
  }, [errsInTagImport, visible]);

  useEffect(() => {
    const {
      tags: swaggerTagsNames,
      discarded: swaggerTagsDiscaded,
    } = getTagsNamesFromSwaggger(swaggerData);
    const hasAssets = isArray(assets) && (assets.length > 0);
    // Don't show error message when invalid tags
    // from auto-generated spec content are discarded.
    // If API doesn't have a swagger file assoicated with it
    // /speccontent would respond with auto-generated swagger
    // If API doesn't have swagger file, then assets would be empty
    if (hasAssets && swaggerTagsDiscaded) {
      setErrsInTagImport(true);
    }

    const allTagsNames = [...allPortalTags, ...createdTags].map(item => item.name);
    const swaggerOnlyTagsNames = differenceWith(swaggerTagsNames, allTagsNames, tagsEqual);
    const swaggerOnlyTags = getDummyTagsFromNames(swaggerOnlyTagsNames);

    // populate labels based on sources
    let allTags = [...allPortalTags, ...createdTags].map(item =>
        ((swaggerTagsNames
          && find(swaggerTagsNames, tagName => tagsEqual(item.name, tagName))) ?
        { ...item, sources: [TAGS_SOURCE_PORTAL, TAGS_SOURCE_SWAGGER] } :
        { ...item, sources: [TAGS_SOURCE_PORTAL] }),
      );
    allTags = allTags.concat(swaggerOnlyTags.map(
      item => ({ ...item, sources: [TAGS_SOURCE_SWAGGER] }),
    ));
    setDefaultListTags(allTags);
    if (!hasSelectionChanged) {
      const apiTagsNames = apiTags.map(item => item.name);
      const defaultSelectedTags = allTags.filter(item => apiTagsNames.includes(item.name));
      setSelectedTags(defaultSelectedTags);
    }
  }, [swaggerData, allPortalTags, apiTags, createdTags]);

  useEffect(() => {
    let updatedSelectedTags = selectedTags;
    // changes to auto-select tag created using add tag button
    if (isCreatingTag) {
      each(newTags, (newTag) => {
        const newTagOption = find(defaultListTags, (item) => (item.name === newTag.name));
        if (!find(selectedTags, (item) => (item.name === newTag.name))) {
          updatedSelectedTags = [...selectedTags, newTagOption];
        }
      });
      setIsCreatingTag(false);
    }
    // updating selected tags to use ids from createdTags, whenever defaultListTags changes
    // this is required when tags are created using swagger tags
    if (createdTags.length > 0) {
      const newSelectedOptions = [];
      let oldSelectedTags = updatedSelectedTags;
      each(createdTags, (createdTag) => {
        const newTagOption = find(defaultListTags, (item) => tagsEqual(item.name, createdTag.name));
        if (find(updatedSelectedTags, (item) => (item.name === createdTag.name))) {
          newSelectedOptions.push(newTagOption);
          oldSelectedTags = filter(oldSelectedTags, item => !tagsEqual(item.name, createdTag.name));
        }
      });
      updatedSelectedTags = [...oldSelectedTags, ...newSelectedOptions];
    }
    if (hasSelectionChanged) {
      setSelectedTags(updatedSelectedTags);
    }
  }, [defaultListTags]);

  const onNextTags = async (tagNames) => {
    await props.saveTagsAssociation(tagNames, apiUuid);
  };

  const associateTags = (tagsNames) => {
    localStorage.setItem('isUnSavedChanges', false);
    setHasUnsavedChanges(false);
    onNextTags(tagsNames);
  };

  useEffect(() => {
    // using setCreatedTags to maintain list of tags
    // created by user in this step instead of pulling
    // all tags after every tag creation
    if (newTags.length > 0) {
      // code to remove duplicates
      // shouldn't happen, but handling it
      // as i noticed the issue during dev testing
      const newTagsMap = reduce([...createdTags, ...newTags],
        (acc, item) => { acc[item.uuid] = item; return acc; },
        {});
      const allNewTags = values(newTagsMap);
      setCreatedTags(allNewTags);
    }
  }, [newTags]);

  useEffect(() => {
    if (newTags.length > 0) {
      // if next is pending
      if (isNextPending) {
        setIsNextPending(false);
        const existingTagsNames = getExistingTagNames(selectedTags);
        const newTagsNames = newTags.map(item => item.name);
        associateTags([...existingTagsNames, ...newTagsNames]);
      }
    }
  }, [newTags]);

  useEffect(() => {
    if (selectedTags.length >= LIMIT_TAGS_ASSOCIATED) {
      const tagAssociationErr =
        intl.getI18nMessage(TAGS_ASSOCIATION_LIMIT_MSG, {
          MAX_TAGS: LIMIT_TAGS_ASSOCIATED,
        });
      notifyMessages(
        tagAssociationErr,
        ERROR_TEXT,
      );
      setShowingTAGLimitErr(true);
    } else if (showingTAGLimitErr) {
      notifyMessages('', '');
      setShowingTAGLimitErr(false);
    }
    if (selectedTags.length > LIMIT_TAGS_ASSOCIATED) {
      setDisableSave(true);
    } else {
      setDisableSave(false);
    }
  }, [selectedTags]);

  const onSearchListChange = (selectedTagsParam) => {
    setHasSelectionChanged(true);
    setSelectedTags([...selectedTagsParam]);
    localStorage.setItem('isUnSavedChanges', true);
    setHasUnsavedChanges(true);
  };

  const onAddItem = (tagText) => {
    setIsCreatingTag(true);
    setHasSelectionChanged(true);
    createTags([{ name: tagText }]);
  };

  const onNext = () => {
    const tagObjects = getSwaggerOnlyTagsNames(selectedTags).map(item => ({ name: item }));
    if (tagObjects.length === 0) {
      associateTags(getExistingTagNames(selectedTags));
      return;
    }
    setIsNextPending(true);
    createTags(tagObjects);
  };

  const toggleSwaggerSelection = (deselectAll) => {
    let updatedSelectedTags = selectedTags;
    if (deselectAll) {
      updatedSelectedTags =
        selectedTags.filter(item => !item.sources.includes(TAGS_SOURCE_SWAGGER));
    } else {
      updatedSelectedTags =
        defaultListTags.filter(item => item.sources.includes(TAGS_SOURCE_SWAGGER));
      updatedSelectedTags = union(updatedSelectedTags, selectedTags);
    }
    setSelectedTags(updatedSelectedTags);
    setHasSelectionChanged(true);
    localStorage.setItem('isUnSavedChanges', true);
    setHasUnsavedChanges(true);
  };

  const renderSubTitleComponent = ({ subtitle }) => {
    const { tags: allSwaggerTags } = getTagsNamesFromSwaggger(swaggerData);
    const selectedTagsNames = selectedTags.map(item => item.name);
    const allSwaggerTagsSelected = differenceWith(
      allSwaggerTags.sort(), selectedTagsNames.sort(), tagsEqual,
    ).length === 0;
    if ((apiDetails.apiServiceType !== 'REST') ||
      (allSwaggerTags.length === 0)) {
      return <span>{subtitle}</span>;
    }
    const selectTagsText = intl.getI18nMessage(TAGS_SELECT_SWAGGER_TAGS);
    const clearTagsText = intl.getI18nMessage(TAGS_UNSELECT_SWAGGER_TAGS);
    return (
      <Fragment>
        <div>{subtitle}</div>
        <Button
          onClick={() => toggleSwaggerSelection(allSwaggerTagsSelected)}
          className={classes.swaggerSelectButton}
          id={'select-swagger-tags'}
        >
          {allSwaggerTagsSelected ? clearTagsText : selectTagsText}
        </Button>
      </Fragment>
    );
  };
  renderSubTitleComponent.propTypes = {
    subtitle: PropTypes.string,
  };

  const tagSubTitle = intl.getI18nMessage(TAGS_SUB_TITLE);
  const placeholderText = intl.getI18nMessage(TAGS_PLACE_HOLDER_TEXT);
  const selectedTagsSubTitle = intl.getI18nMessage(TAGS_SELECTED_TAG_SUB_TITLE);
  const addNewTagText = intl.getI18nMessage(TAGS_ADD_NEW_TAG);
  const tagsInvalidError = intl.getI18nMessage(TAGS_INVALID_ERROR, { MAX_TAG_LENGTH });
  return (
    <div id="api-tags-container">
      <h1 className={classes.pageTitle}>Tags</h1>
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={6} item>
          <SearchTextListContainer
            title={intl.getI18nMessage('label.api.tags.available')}
            subtitle={tagSubTitle}
            placeHolderText={placeholderText}
            defaultList={defaultListTags}
            defaultSelectedList={selectedTags}
            listId={'tags-list'}
            isCheckBoxVisible
            disableSelection={disableSave}
            onSearchListChange={onSearchListChange}
            userContext={{}}
            canAddItem
            addItemText={addNewTagText}
            onAddItem={onAddItem}
            getItemSecondaryText={(item) => getItemSecondaryText(item, portalText, swaggerText)}
            validateItem={validateTag}
            validationErrorMsg={tagsInvalidError}
            SubTitleComponent={renderSubTitleComponent}
          />
        </Grid>
        <Grid md={6} item>
          <SearchTextListContainer
            title={intl.getI18nMessage('label.api.tags.selected')}
            subtitle={selectedTagsSubTitle}
            placeHolderText={placeholderText}
            isSelectedNumberVisible
            defaultList={selectedTags}
            defaultSelectedList={selectedTags}
            isMandatory={false}
            listId={'tags-selected-list'}
            onSearchListChange={onSearchListChange}
            isDeleteIconVisible
            userContext={{}}
            getItemSecondaryText={(item) => getItemSecondaryText(item, portalText, swaggerText)}
          />
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={SAVE_TEXT}
        onCancelClick={() => onCancel(hasUnsavedChanges)}
        isDisabled={disableSave}
        id="tag-form-buttons"
      />
    </div>
  );
}

Tags.propTypes = {
  classes: PropTypes.shape({}),
  errors: PropTypes.arrayOf(PropTypes.shape({})),
  apiUuid: PropTypes.string,
  allPortalTags: PropTypes.arrayOf(PropTypes.shape({})),
  apiTags: PropTypes.arrayOf(PropTypes.shape({})),
  assets: PropTypes.arrayOf(PropTypes.shape({})),
  newTags: PropTypes.arrayOf(PropTypes.shape({})),
  createTags: PropTypes.func,
  onCancel: PropTypes.func,
  swaggerData: PropTypes.shape({}),
  apiDetails: PropTypes.shape({}),
  notifyMessages: PropTypes.func,
  visible: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/pages/Api/Edit/Tags/index.js